import { useState, useEffect } from 'react'
import { CalendarDays, Clock, Sparkles, Calendar, Building2, Globe, MapPin, Info, Inbox } from 'lucide-react'
import { holidaysAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { formatDate } from '../../lib/dateFormat'

const TYPE_STYLES: Record<string, { chip: string; dot: string; icon: any; label: string }> = {
  NATIONAL: { chip: 'bg-blue-50 text-blue-700 ring-blue-200',       dot: 'bg-blue-500',    icon: Globe,     label: 'National' },
  STATE:    { chip: 'bg-violet-50 text-violet-700 ring-violet-200', dot: 'bg-violet-500',  icon: MapPin,    label: 'State' },
  COMPANY:  { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-500', icon: Building2, label: 'Company' },
  OPTIONAL: { chip: 'bg-amber-50 text-amber-700 ring-amber-200',    dot: 'bg-amber-500',   icon: Info,      label: 'Optional' },
}

const DEFAULT_TYPE = { chip: 'bg-neutral-100 text-neutral-700 ring-neutral-200', dot: 'bg-neutral-400', icon: Calendar, label: 'Holiday' }

export default function HolidayCalendarPage() {
  const { error: toastError } = useToast()
  const [upcoming, setUpcoming] = useState<any[]>([])
  const [yearHolidays, setYearHolidays] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [year, setYear] = useState(new Date().getFullYear())

  const load = async (selectedYear = year) => {
    setIsLoading(true)
    try {
      const [upcomingRes, calendarRes] = await Promise.all([
        holidaysAPI.getUpcoming(),
        holidaysAPI.getCalendar(selectedYear),
      ])
      setUpcoming(upcomingRes.data.results || upcomingRes.data)
      setYearHolidays(calendarRes.data.results || calendarRes.data)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load the holiday calendar.'))
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load(year)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year])

  const yearOptions = [year - 1, year, year + 1]

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="mb-7">
        <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
          <Sparkles className="w-3 h-3" />
          Calendar
        </div>
        <h1
          className="text-3xl font-bold text-neutral-900 mb-1"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          Holiday Calendar
        </h1>
        <p className="text-sm text-neutral-500">Upcoming and yearly holidays that apply to you</p>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-2xl ring-1 ring-neutral-200/70 py-20 flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
          <p className="text-sm text-neutral-500">Loading holidays...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* ── Upcoming Holidays ── */}
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                <Clock className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                  Upcoming Holidays
                </h2>
                <p className="text-xs text-neutral-500">Next celebrations coming up</p>
              </div>
            </div>

            <div className="p-5">
              {upcoming.length === 0 ? (
                <div className="py-8 flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-2xl bg-neutral-100 flex items-center justify-center">
                    <Inbox className="w-6 h-6 text-neutral-400" />
                  </div>
                  <p className="text-sm text-neutral-500">No upcoming holidays</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {upcoming.map((h) => {
                    const style = TYPE_STYLES[h.holiday_type] || DEFAULT_TYPE
                    const TypeIcon = style.icon
                    return (
                      <div
                        key={h.id}
                        className="group flex items-start gap-3 p-3.5 rounded-xl border border-neutral-100 hover:bg-[#FAF6EE]/70 hover:border-neutral-200 transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 ${style.chip}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-neutral-900 truncate">{h.name}</p>
                          <p className="text-xs text-neutral-500 inline-flex items-center gap-1 tabular-nums">
                            <Calendar className="w-3 h-3" />
                            {formatDate(h.date)}
                          </p>
                          {h.description && (
                            <p className="text-xs text-neutral-500 mt-1 line-clamp-2">{h.description}</p>
                          )}
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ring-1 whitespace-nowrap ${style.chip}`}>
                          <span className={`w-1 h-1 rounded-full ${style.dot}`} />
                          {style.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Full Year ── */}
          <div className="bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md shadow-blue-500/30">
                  <CalendarDays className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-neutral-900" style={{ fontFamily: 'Georgia, serif' }}>
                    Year Holidays
                  </h2>
                  <p className="text-xs text-neutral-500">{yearHolidays.length} {yearHolidays.length === 1 ? 'holiday' : 'holidays'}</p>
                </div>
              </div>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="px-3 py-1.5 bg-neutral-50 border border-neutral-200 rounded-xl text-sm font-medium text-neutral-900 focus:bg-white focus:ring-4 focus:ring-[#E4670F]/10 focus:border-[#E4670F] outline-none transition-all cursor-pointer"
              >
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div className="p-5 max-h-[560px] overflow-y-auto">
              {yearHolidays.length === 0 ? (
                <div className="py-8 flex flex-col items-center gap-2">
                  <div className="w-12 h-12 rounded-2xl bg-neutral-100 flex items-center justify-center">
                    <Inbox className="w-6 h-6 text-neutral-400" />
                  </div>
                  <p className="text-sm text-neutral-500">No holidays found for {year}</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {yearHolidays.map((h) => {
                    const style = TYPE_STYLES[h.holiday_type] || DEFAULT_TYPE
                    const TypeIcon = style.icon
                    return (
                      <div
                        key={h.id}
                        className="group flex items-start gap-3 p-3.5 rounded-xl border border-neutral-100 hover:bg-[#FAF6EE]/70 hover:border-neutral-200 transition-colors"
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0 ${style.chip}`}>
                          <TypeIcon className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-neutral-900 truncate">{h.name}</p>
                          <p className="text-xs text-neutral-500 inline-flex items-center gap-1 tabular-nums">
                            <Calendar className="w-3 h-3" />
                            {formatDate(h.date)}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ring-1 whitespace-nowrap ${style.chip}`}>
                          <span className={`w-1 h-1 rounded-full ${style.dot}`} />
                          {style.label}
                        </span>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}